'use client'

// src/components/sat/SectionReviewPage.tsx
//
// Bluebook-style review surface for a SAT module. Shown when the
// student reaches the last question and clicks "Review" (or any
// time we want to give them a last look before submitting).
//
// Replaces the question card area in Runner.tsx — the timer header,
// flag tool, calculator, and the always-present "End module" safety
// button stay on screen so the test feels continuous and the timer
// keeps ticking.
//
// Mirrors the official Digital SAT (Bluebook) flow:
//   - Shows every question in a grid with status indicators:
//       ✓ answered
//       ○ unanswered
//       ⚑ flagged ("marked for review")
//   - Click any question to jump back to it.
//   - "Submit Section" button to officially end the module.
//
// Deliberately presentational only — all state lives in Runner.tsx.
// Easier to test in isolation, and the parent can decide how
// "Submit Section" wires up (in our case it calls the existing
// onEndModuleEarlySafe handler so there's one end-module code
// path).

import { cn } from '@/lib/utils'
import { Button } from '@/components/ui/button'
import { type SaveStates, statusOf, unsavedCount as countUnsaved } from '@/lib/sat/save-status'

export type ReviewItem = {
  id: string
  /** Keys the save status. Absent on surfaces that don't save per question. */
  questionId?: string
  orderIndex: number
  choice?: string | null
  numericAnswer?: string | null
  flagged: boolean
}

interface SectionReviewPageProps {
  items: ReviewItem[]
  /** When the student clicks a question tile, jump back to it. */
  onSelect: (idx: number) => void
  /** "Submit Section" — wire to the same handler that ends the module. */
  onSubmit: () => void
  /** True while the end-module request is in flight. */
  finishing?: boolean
  /**
   * Match the Runner's practice / real-test wording: in practice
   * mode the action is "Finish Practice" rather than "Submit Section."
   */
  practiceMode?: boolean
  /**
   * Override the submit wording entirely. The free diagnostic reuses this
   * screen but has no "section" to submit — it ends with a score — so it passes
   * its own label rather than forking the component.
   */
  submitLabel?: string
  /**
   * Which answers the server hasn't confirmed (see save-status.ts). Omitted by
   * surfaces without per-question saves, which then show no save marks.
   */
  saveStates?: SaveStates
}

export default function SectionReviewPage({
  items,
  onSelect,
  onSubmit,
  finishing = false,
  practiceMode = false,
  submitLabel,
  saveStates = {},
}: SectionReviewPageProps) {
  // Status counts drive the top-of-page summary.
  const answeredCount = items.filter((it) => isAnswered(it)).length
  const unansweredCount = items.length - answeredCount
  const flaggedCount = items.filter((it) => it.flagged).length
  const unsaved = (it: ReviewItem) =>
    !!it.questionId && statusOf(saveStates, it.questionId) === 'unsaved'
  const unsavedCount = countUnsaved(saveStates)

  return (
    <section
      className="force-light bg-white rounded-lg p-4 md:p-6"
      aria-label="Section review"
      data-testid="section-review-page"
    >
      <div className="mb-4">
        <h2 className="text-xl font-semibold text-slate-900">Review your answers</h2>
        <p className="mt-1 text-sm text-slate-600">
          Click any question to go back to it, or submit when you’re ready.
        </p>
      </div>

      {/* Status summary — leads with the number the student cares about most:
          how many they LEFT BLANK. Warning-coloured when > 0 so they can't
          miss it. */}
      <dl
        className="mb-4 grid grid-cols-3 gap-2 text-center"
        aria-label="Status summary"
        data-testid="status-summary"
      >
        <StatusTile label="Answered" count={answeredCount} tone="ok" />
        <StatusTile
          label="Unanswered"
          count={unansweredCount}
          tone={unansweredCount > 0 ? 'warn' : 'ok'}
        />
        <StatusTile label="Flagged" count={flaggedCount} tone={flaggedCount > 0 ? 'flag' : 'ok'} />
      </dl>

      {/* Legend — explicit even though the colours are reused from the
          existing Question Map sidebar. Students reach the review page
          under pressure; a 1-line legend is worth the screen real
          estate. */}
      <ul
        className="mb-3 flex flex-wrap gap-3 text-xs text-slate-600"
        aria-label="Status legend"
        data-testid="status-legend"
      >
        <li className="flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded border bg-emerald-50 border-emerald-300" />
          Answered
        </li>
        <li className="flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded border bg-white border-slate-200" />
          Unanswered
        </li>
        <li className="flex items-center gap-1">
          <span className="inline-block h-3 w-3 rounded border bg-white border-amber-400" />
          Flagged
        </li>
        {unsavedCount > 0 && (
          <li className="flex items-center gap-1 font-medium text-red-700">
            <span aria-hidden className="text-[11px] leading-none">
              !
            </span>
            Not saved yet
          </li>
        )}
      </ul>

      {/* Question grid — same 6-col layout + colour scheme as the
          existing desktop Question Map sidebar in Runner.tsx. Reusing
          the same visual language means students don't have to learn
          a new affordance on the review screen. */}
      <div
        className="grid grid-cols-5 gap-2 sm:grid-cols-6 md:grid-cols-8"
        role="navigation"
        aria-label="Jump to question"
      >
        {items.map((it, i) => {
          const answered = isAnswered(it)
          const notSaved = unsaved(it)
          return (
            <button
              key={it.id}
              type="button"
              data-testid={`review-tile-${i}`}
              data-answered={answered ? 'true' : 'false'}
              data-flagged={it.flagged ? 'true' : 'false'}
              data-unsaved={notSaved ? 'true' : 'false'}
              onClick={() => onSelect(i)}
              aria-label={`Question ${i + 1}${answered ? ', answered' : ', unanswered'}${
                it.flagged ? ', flagged' : ''
              }${notSaved ? ', not saved yet' : ''}`}
              className={cn(
                'relative flex h-12 min-h-[44px] items-center justify-center rounded border text-sm font-medium bg-white text-slate-900',
                answered ? 'bg-emerald-50 border-emerald-300' : 'bg-white border-slate-300',
                it.flagged && 'border-amber-400',
                notSaved && 'border-2 border-dashed border-red-500',
              )}
              title={notSaved ? 'Not saved yet — retrying' : it.flagged ? 'Flagged for review' : ''}
            >
              {i + 1}
              {it.flagged && (
                <span
                  aria-hidden
                  className="absolute -top-1 -right-1 text-[10px] leading-none text-amber-600"
                >
                  ⚑
                </span>
              )}
              {notSaved && (
                <span
                  aria-hidden
                  className="absolute -bottom-1 -right-1 rounded-full bg-red-600 px-1 text-[10px] font-bold leading-tight text-white"
                >
                  !
                </span>
              )}
            </button>
          )
        })}
      </div>

      {/* Submit area — separated from the grid by a thick divider so
          there's no accidental tap. The "you have N blank" line below
          the button is the last-chance warning. Style stays in
          Bluebook range (no scary red); we trust the student to read
          the count and decide. */}
      <div className="mt-6 border-t border-slate-200 pt-4">
        <Button
          type="button"
          className="sat-btn-primary min-h-[44px] w-full sm:w-auto"
          onClick={onSubmit}
          disabled={finishing}
          data-testid="submit-section-button"
        >
          {finishing
            ? 'Submitting…'
            : (submitLabel ?? (practiceMode ? 'Finish Practice' : 'Submit Section'))}
        </Button>
        {unsavedCount > 0 && !finishing && (
          <p
            className="mt-2 text-sm font-medium text-red-700"
            role="status"
            data-testid="unsaved-warning"
          >
            {unsavedCount === 1 ? "1 answer isn't" : `${unsavedCount} answers aren't`} saved yet
            (marked !). We&apos;re retrying — keep this page open. Submitting before they save would
            count them as unanswered.
          </p>
        )}
        {unansweredCount > 0 && !finishing && (
          <p className="mt-2 text-sm text-amber-700" role="status" data-testid="unanswered-warning">
            You have {unansweredCount} unanswered question{unansweredCount === 1 ? '' : 's'}. Click
            a question above to go back.
          </p>
        )}
      </div>
    </section>
  )
}

function isAnswered(it: ReviewItem): boolean {
  if (it.choice && it.choice !== '') return true
  if (it.numericAnswer && it.numericAnswer !== '') return true
  return false
}

function StatusTile({
  label,
  count,
  tone,
}: {
  label: string
  count: number
  tone: 'ok' | 'warn' | 'flag'
}) {
  const cls =
    tone === 'warn'
      ? 'border-amber-300 bg-amber-50 text-amber-900'
      : tone === 'flag'
        ? 'border-amber-300 bg-amber-50 text-amber-900'
        : 'border-slate-200 bg-slate-50 text-slate-900'
  return (
    <div
      className={cn('rounded border p-2', cls)}
      data-testid={`status-tile-${label.toLowerCase()}`}
    >
      <dt className="text-[11px] uppercase tracking-wide text-slate-600">{label}</dt>
      <dd className="text-xl font-semibold leading-tight">{count}</dd>
    </div>
  )
}
